import { useState } from 'react'
import { Icon } from '@iconify/react'
import { useInfiniteQuery } from '@tanstack/react-query'
import InfiniteScroll from 'react-infinite-scroll-component'
import { Empty, Spin, useDebounceState } from '@pepper/ui'
import { getProjects } from '@pepper/utils/src/api/projects/getProjects'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useProjectDomainMap } from 'utils/hooks/useProjectDomainMap'

import createProjectArrowSrc from 'assets/images/home/guide_arrow.svg'

import Option from './Option'

import s from './ProjectPicker.module.css'

const PAGE_SIZE = 15
const DEBOUNCE_DELAY = 500

type Props = {
  onProjectClick: (projectId: string, isDomainConnected: boolean) => void
  activeDomainId?: string
  onCreateNew?: () => void
  listHeightPx?: number
}

export const ProjectPicker = ({
  onProjectClick,
  onCreateNew,
  activeDomainId,
  listHeightPx,
}: Props) => {
  const companyId = useSelectedCompanyId()

  const [searchText, setSearchText] = useState('')
  const [selectedProjectId, setSelectedProjectId] = useState('')
  const debouncedSearchValue = useDebounceState<string>(
    searchText,
    DEBOUNCE_DELAY
  )

  const {
    data: projects,
    error: projectsError,
    isLoading: isProjectsLoading,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: ['project-picker-projects', companyId, debouncedSearchValue],
    queryFn: async ({ pageParam = 0 }) => {
      return getProjects({
        page: pageParam,
        page_size: PAGE_SIZE,
        query_params: {
          company_id: companyId,
          search_title: debouncedSearchValue,
        },
      })
    },
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage?.data?.total_records ?? 0

      if (total > 0 && total > allPages.length * PAGE_SIZE) {
        return allPages.length + 1
      }
    },
  })

  const { domainMap } = useProjectDomainMap({
    companyId,
  })

  const handleProjectClick = (
    projectId: string,
    isDomainConnected: boolean
  ) => {
    setSelectedProjectId(projectId)
    onProjectClick(projectId, isDomainConnected)
  }

  return (
    <div className={s.wrapper}>
      <div className={s.search}>
        <input
          className={s.input}
          value={searchText}
          placeholder="Search a Project..."
          onChange={e => setSearchText(e.target.value)}
          autoFocus={true}
        />
        <Icon className={s.icon} icon="lucide:search" width={18} height={18} />
      </div>
      {!isProjectsLoading && !projects?.pages?.[0]?.data?.records?.length ? (
        <div className={s.noFoundHeaderText}>
          No results found for ‘{searchText}’
        </div>
      ) : null}

      {projectsError && (
        <Empty description="Something went wrong while fetching projects list" />
      )}
      {!isProjectsLoading && !projects?.pages?.[0]?.data?.records?.length ? (
        <div
          className={s.list}
          style={{
            maxHeight: `${listHeightPx}px`,
          }}
        >
          <div className={s.emptyGuidingArrow}>
            <img
              className={s.emptyGuidingArrowImg}
              src={createProjectArrowSrc}
              alt="arrow"
            />
            <p>
              Project with this name does not exist. <br /> You can always
              create a new project.
            </p>
          </div>
        </div>
      ) : (
        <Spin spinning={isProjectsLoading}>
          <div
            className={s.list}
            id="project_list"
            style={{
              maxHeight: `${listHeightPx}px`,
            }}
          >
            {projects && (
              <InfiniteScroll
                hasMore={!!hasNextPage}
                next={fetchNextPage}
                dataLength={
                  projects.pages?.flatMap(p => p.data?.records).length ?? 0
                }
                scrollableTarget="project_list"
                loader={<Spin className={s.spin} />}
              >
                {projects.pages.map(page =>
                  page.data?.records.map(project => {
                    const domain = domainMap?.[project.id]?.[0]?.domain
                    return (
                      <Option
                        key={project.id}
                        domain={domain}
                        title={project.project_title}
                        projectId={String(project.project_code)}
                        itemsCount={project.number_of_items}
                        topicsCount={
                          domainMap?.[project.id]?.[0]?.total_topics ?? 0
                        }
                        onClick={() => handleProjectClick(project.id, !!domain)}
                        activeDomainId={activeDomainId}
                        isSelected={project.id === selectedProjectId}
                      />
                    )
                  })
                )}
              </InfiniteScroll>
            )}
          </div>
        </Spin>
      )}
      {onCreateNew && (
        <button className={s.createBtn} onClick={onCreateNew} type="button">
          <Icon icon="lucide:plus" width={16} height={16} />
          Create new Project
        </button>
      )}
    </div>
  )
}
